import React, { useEffect, useState } from 'react';
import { Scale } from 'lucide-react';
import api from '../services/api';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import { notificaErrore, notificaConferma, messaggioDa } from '../utils/notifiche';
import { partiDelMarchio } from '../utils/marchio';
import { rinnovaMarchio } from '../hooks/useMarchio';
import '../styles/DatiFiscali.css';

/**
 * DatiFiscali — i dati della societa' per la fattura elettronica e i codici
 * di pagamento (blocco 6, migration 258). Solo l'amministrazione.
 *
 * E' un modulo con il pulsante «Salva», non campi che si salvano da soli:
 * sono impostazioni fiscali (la regola di Alex del 24/09 le lascia al
 * pulsante), e un regime cambiato a meta' non deve finire in una fattura.
 * Le regole di ogni campo sono quelle del server (services/datiFiscali.js):
 * un valore che la fattura elettronica non accetterebbe non si salva, e il
 * messaggio dice perche'.
 *
 * La partita IVA della struttura si MOSTRA e non si modifica (Alex,
 * 20/10/2026 sera: «la P.IVA si MOSTRA in sola lettura, non si modifica dalla
 * schermata. Si scrive solo con installa.js»): non e' un campo del modulo,
 * cosi' «Salva» non la rimanda indietro, e il server la rifiuta comunque. Se
 * non andrebbe alla prima fattura, il motivo lo dice il server, con le parole
 * della fattura.
 *
 * IL NOME IN CIMA AL MENU (Alex, 7/10/2026, risposta B4: «campo del nome del
 * marchio in "Dati fiscali"»): il parametro marchio_nome, con un salvataggio
 * suo (PUT /dati-fiscali/marchio). Non sta fra i campi della societa': quelli
 * il server li porta in maiuscolo, e le maiuscole del nome decidono la parte
 * in evidenza. L'anteprima usa la regola del menu (utils/marchio.js), non una
 * copia; salvato, il menu di questa pagina lo scrive subito (rinnovaMarchio).
 */
const CODICI_MP = Array.from({ length: 23 }, (_, i) => `MP${String(i + 1).padStart(2, '0')}`);

const DatiFiscali = () => {
    const [campi, setCampi] = useState(null);
    const [valori, setValori] = useState({});
    const [metodi, setMetodi] = useState([]);
    const [piva, setPiva] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [marchio, setMarchio] = useState(null);
    const [nome, setNome] = useState('');
    const [nomeInCorso, setNomeInCorso] = useState(false);
    /* La lettura riprova da sola una volta, poi lo dice nella pagina con
       «Riprova» (primaLettura.js); fino al 3/11 era una notifica, che
       un pulsante per rileggere non ce l'ha. I salvataggi restano notifiche. */
    const [erroreLettura, setErroreLettura] = useState(null);

    /* conNome: false dopo «Salva» dei dati fiscali, che non tocca il nome:
       un nome scritto e non ancora salvato resta nel suo campo. */
    const carica = async (conNome = true) => {
        try {
            const r = await conUnTentativo(() => api.get('/dati-fiscali'));
            setCampi(r.data.data.campi);
            const v = {};
            r.data.data.campi.forEach((c) => { v[c.chiave] = c.valore; });
            setValori(v);
            setMetodi(r.data.data.metodi);
            setPiva(r.data.data.partita_iva || null);
            /* Il server di prima non lo manda: allora il riquadro non c'e'. */
            const m = r.data.data.marchio || null;
            setMarchio(m);
            if (conNome) setNome(m ? m.valore : '');
            setErroreLettura(null);
        } catch (e) {
            setErroreLettura(motivoDellaLettura(e, 'Non riesco a leggere i dati fiscali'));
        }
    };
    useEffect(() => { carica(); }, []);

    const salva = async () => {
        setInCorso(true);
        try {
            const r = await api.put('/dati-fiscali', valori);
            notificaConferma(r.data.message);
            await carica(false);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare i dati fiscali'));
        } finally {
            setInCorso(false);
        }
    };

    const salvaNome = async () => {
        setNomeInCorso(true);
        try {
            const r = await api.put('/dati-fiscali/marchio', { marchio_nome: nome });
            const salvato = r.data.data ? r.data.data.marchio : null;
            setNome(salvato || '');
            setMarchio((m) => ({ ...m, valore: salvato || '' }));
            rinnovaMarchio(salvato);
            notificaConferma(r.data.message);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare il nome in cima al menu'));
        } finally {
            setNomeInCorso(false);
        }
    };

    /* Un codice di pagamento e' un gesto solo: si salva alla scelta. */
    const salvaMetodo = async (codice, codiceMp) => {
        try {
            await api.put(`/dati-fiscali/metodi/${encodeURIComponent(codice)}`, { codice_mp: codiceMp });
            setMetodi((m) => m.map((x) => (x.codice === codice ? { ...x, codice_mp: codiceMp || null } : x)));
            notificaConferma('Codice salvato.');
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a salvare il codice'));
        }
    };

    if (!campi) return null;
    const anteprima = partiDelMarchio(nome);
    return (
        <div className="dfi-pagina">
            <IntestazioneSezione titolo="Dati fiscali"
                icona={<Scale size={20} aria-hidden="true" />}
                sottotitolo="La società nella fattura elettronica, e il codice di ogni metodo di pagamento." />
            <ErroreLettura testo={erroreLettura} onRiprova={() => carica()} />

            <section className="dfi-riquadro">
                <h3>La società</h3>
                <p className="dfi-aiuto">
                    Entrano in ogni fattura elettronica. Il regime fiscale e lo stato di liquidazione servono
                    sempre; il REA per una società iscritta; l&apos;IBAN solo per i bonifici.
                </p>
                {piva && (
                    <div className="dfi-sola-lettura">
                        <span>{piva.etichetta}</span>
                        <strong className="rm-numeri">{piva.valore || '—'}</strong>
                        {piva.problema && <span className="dfi-problema" role="alert">{piva.problema}</span>}
                        <p className="dfi-aiuto">
                            La partita IVA si scrive all&apos;installazione e da qui non si modifica: per
                            correggerla serve l&apos;assistenza.
                        </p>
                    </div>
                )}
                <div className="dfi-campi">
                    {campi.map((c) => (
                        <label key={c.chiave} className="dfi-campo">
                            <span>{c.etichetta}</span>
                            <input value={valori[c.chiave] ?? ''}
                                   onChange={(e) => setValori((v) => ({ ...v, [c.chiave]: e.target.value }))} />
                        </label>
                    ))}
                </div>
                <button type="button" className="dfi-btn" onClick={salva} disabled={inCorso}>Salva</button>
            </section>

            <section className="dfi-riquadro">
                <h3>Metodi di pagamento</h3>
                <p className="dfi-aiuto">
                    Il codice che la fattura elettronica scrive per ogni incasso. Un metodo senza codice rende
                    «da correggere» la fattura che lo usa: il gestionale non ne sceglie uno a caso.
                    «Tracciato» è quello che il Sistema TS legge per il pagamento tracciato: stessa riga, una
                    fonte sola.
                </p>
                <div className="rm-scorre">
                    <table className="dfi-tabella">
                        <thead>
                            <tr><th>Metodo</th><th>Tracciato</th><th>Codice</th></tr>
                        </thead>
                        <tbody>
                            {metodi.map((m) => (
                                <tr key={m.codice}>
                                    <td>{m.descrizione}{!m.attivo && <em> (non attivo)</em>}</td>
                                    <td>{m.tracciato ? 'sì' : 'no'}</td>
                                    <td>
                                        <select value={m.codice_mp || ''} aria-label={`Codice di ${m.descrizione}`}
                                                onChange={(e) => salvaMetodo(m.codice, e.target.value)}>
                                            <option value="">— nessuno —</option>
                                            {CODICI_MP.map((c) => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {marchio && (
                <section className="dfi-riquadro" data-riquadro="marchio">
                    <h3>{marchio.etichetta}</h3>
                    <p className="dfi-aiuto">
                        Il nome scritto in cima al menu dello stile nuovo. Se finisce con due o più lettere
                        maiuscole, e prima c&apos;è almeno una minuscola, quelle maiuscole vanno in evidenza.
                        Vuoto: il menu scrive «Gestionale». Non entra nelle fatture né negli altri documenti.
                    </p>
                    <div className="dfi-campi">
                        <label className="dfi-campo">
                            <span>{marchio.etichetta}</span>
                            <input value={nome} onChange={(e) => setNome(e.target.value)} />
                        </label>
                        <div className="dfi-campo">
                            <span>Come esce nel menu</span>
                            <span className="dfi-marchio-anteprima">
                                {anteprima.corpo}
                                {anteprima.evidenza && <span className="dfi-marchio-evidenza">{anteprima.evidenza}</span>}
                            </span>
                        </div>
                    </div>
                    <button type="button" className="dfi-btn" onClick={salvaNome} disabled={nomeInCorso}>Salva il nome</button>
                </section>
            )}
        </div>
    );
};

export default DatiFiscali;
